import { useEffect, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { Card, CardBody } from 'components/card';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import { Button } from 'components/button';
import { useFormikContext } from 'formik';
import { CardHeader, CardHeaderTitle } from 'components/card/card-header';
import FormEditHeaderButtons from 'containers/partials/buttons/FormEditHeaderButtons';
import Icon from 'components/icon/Icon';
import {
  ROUTE_PREVENTION_ENTITY_EDIT_PREVENTION,
  ROUTE_SESSIONS_ENTITY
} from 'routes/RouteList';
import { useSessionContext } from 'contexts/SessionContext';
import FormEditFooterButtons from 'containers/partials/buttons/FormEditFooterButtons';
import { useTestsContext } from 'contexts/TestsContext';
import useEdit from 'hooks/utils/useEdit';
import SessionDetailInput from 'containers/pages/sessions/SessionDetailInput';
import SessionTestsCard from 'containers/pages/sessions/SessionTestsCard';
import RecurringSessionDetailExtras from 'containers/pages/sessions/RecurringSessionDetailExtras';
import SessionDetailExtras from 'containers/pages/sessions/SessionDetailExtras';
import PreventionDetailExtras from 'containers/pages/prevention/PreventionDetailExtras';
import PreventionSelectedTestsCard from 'containers/pages/prevention/PreventionSelectedTestsCard';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import SessionLinks from 'containers/pages/sessions/components/SessionLinks';
import { TestSetV2Type } from 'constants';
import DownloadEmptyFormButton from 'containers/partials/buttons/DownloadEmptyFormButton';
import ExportReportButton from 'containers/partials/session-report-buttons/ExportReportButton';
import DownloadReportsButton from 'containers/partials/session-report-buttons/DownloadReportsButton';
import CopySessionButton from 'containers/partials/buttons/CopySessionButton';
import RemoveSessionButton from 'containers/partials/buttons/RemoveSessionButton';
import DownloadReportOptionsModal from 'containers/partials/modals/DownloadReportOptionsModal';
import MailReportOptionsModal from 'containers/partials/modals/MailReportOptionsModal';
import slugify from 'slugify';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import AddPreventionModal from 'containers/pages/prevention/AddPreventionModal';

function SessionDetailTab({ editPanel = false, selectedPersons, ...props }) {
  const ability = useAbility(AbilityContext);
  const { push } = useHistory();
  const { session } = useSessionContext();
  const { entityId } = useParams();
  const { submitForm, handleReset, isValid, isSubmitting } = useFormikContext();
  const { testActions, loading } = useTestsContext();
  const [edit, setEdit] = useEdit(props.edit, true);
  const [copySessionModal, setCopySessionModal] = useState(false);

  const { setMenuItems, clearAllContext } = useContextMenu();

  const [showReportOptionsModal, setShowReportOptionsModal] = useState(false);
  const [showMailReportOptionsModal, setShowMailReportOptionsModal] =
    useState(false);

  useEffect(() => {
    testActions.getTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const contextButtons = [];

    if (ability.can('manage', 'Sessions') && session.isPrevention) {
      contextButtons.push(<AddExtraTestsBtn menu />);
    }

    if (ability.can('download-form', 'Sessions')) {
      contextButtons.push(<DownloadEmptyFormButton session={session} />);
    }

    if (
      ability.can('export', 'Sessions') &&
      session.finishedPercentage !== 0 &&
      !session.isRecurring
    ) {
      contextButtons.push(<ExportReportButton session={session} />);
    }

    if (session.finishedPercentage !== 0 && !session.isRecurring) {
      contextButtons.push(
        <DownloadReportsButton
          type={'group'}
          modalCallback={callBack => setShowReportOptionsModal(callBack)}
        />
      );
    }
    if (ability.can('copy', 'Sessions')) {
      contextButtons.push(
        <CopySessionButton
          menu
          entityId={entityId}
          session={session}
          onCopySession={session => {
            setCopySessionModal(session);
          }}
        />
      );
    }

    if (ability.can('delete', 'Sessions')) {
      contextButtons.push(
        <RemoveSessionButton
          menu
          entityId={entityId}
          sessionId={session.id}
          onComplete={() =>
            push(ROUTE_SESSIONS_ENTITY.replace(':entityId', entityId))
          }
          dataQa={`${slugify(
            messages.menuItemSessionRemoveSession.id.toLowerCase()
          )}-menu`}
        >
          <FormattedMessage {...messages.menuItemSessionRemoveSession} />
        </RemoveSessionButton>
      );
    }
    if (ability.can('manage', 'Sessions') && session.isPrevention) {
      // addActionButton(<AddExtraTestsBtn primary rounded />);
    }

    setMenuItems(contextButtons);
    return () => {
      // Cleanup action buttons and context menu items
      clearAllContext([]);
    };
  }, [session]);

  useEffect(() => {
    if (!loading) {
      testActions.setSelectedTests(session.tests);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.tests, loading]);

  const onEditPreventionHandler = () => {
    push(
      generatePath(ROUTE_PREVENTION_ENTITY_EDIT_PREVENTION, {
        entityId,
        sessionId: session.id,
        type: session.preventionType
      })
    );
  };

  return (
    <>
      {session.type !== TestSetV2Type.PREVENTION && !session.isRecurring && (
        <SessionLinks entityId={entityId} sessionId={session.id} />
      )}
      <Card secondary multiBody>
        <CardHeader secondary>
          <CardHeaderTitle>
            <FormattedMessage {...messages.titleSessionDetails} />
          </CardHeaderTitle>
          {ability.can('manage', 'Sessions') && (
            <FormEditHeaderButtons
              edit={edit}
              setEdit={setEdit}
              onCancel={handleReset}
              onSubmit={submitForm}
              type={'button'}
            />
          )}
        </CardHeader>
        <CardBody secondary>
          <SessionDetailInput entityId={entityId} edit={edit} />
          {session.isCustom && !session.isRecurring && (
            <SessionDetailExtras entityId={entityId} edit={edit} />
          )}
          {session.isRecurring && (
            <RecurringSessionDetailExtras entityId={entityId} edit={edit} />
          )}
          {session.isPrevention && (
            <PreventionDetailExtras entityId={entityId} edit={edit} />
          )}
        </CardBody>
        {editPanel && (
          <FormEditFooterButtons
            edit={edit}
            setEdit={setEdit}
            disabledSubmit={!isValid || isSubmitting}
            onCancel={handleReset}
            onSubmit={submitForm}
            type={'button'}
          />
        )}
      </Card>
      {(session.isCustom || session.isRecurring) && session.isTalentV2 && (
        <SessionTestsCard
          childrenOnTop
          extraClassNames={'u-padding-vertical-medium'}
        />
      )}
      {session.isPrevention && (
        <PreventionSelectedTestsCard
          childrenOnTop
          edit={edit}
          onEdit={edit => setEdit(edit)}
          extraClassNames={'u-padding-vertical-medium'}
        >
          {ability.can('manage', 'Sessions') && (
            <Button
              extraClassNames="u-margin-left-auto"
              small
              ghost
              onClick={onEditPreventionHandler}
            >
              <Icon id="edit" />
              <FormattedMessage {...messages.fullEditButton} />
            </Button>
          )}
        </PreventionSelectedTestsCard>
      )}
      {showMailReportOptionsModal && (
        <MailReportOptionsModal
          onClose={() => setShowMailReportOptionsModal(false)}
        />
      )}
      {showReportOptionsModal && (
        <DownloadReportOptionsModal
          callback={showReportOptionsModal}
          onClose={() => setShowReportOptionsModal(false)}
        />
      )}
      {copySessionModal && (
        <AddPreventionModal
          entityId={entityId}
          session={copySessionModal}
          isTemplate={false}
          onClose={() => {
            setCopySessionModal(false);
          }}
          onComplete={sessionId => {
            push(
              generatePath(ROUTE_PREVENTION_ENTITY_EDIT_PREVENTION, {
                entityId,
                sessionId
              })
            );
          }}
        />
      )}
    </>
  );
}

export default SessionDetailTab;

const AddExtraTestsBtn = props => {
  const { entityId } = useParams();
  const { push } = useHistory();
  const { session } = useSessionContext();

  const onEditPreventionHandler = () => {
    push(
      generatePath(ROUTE_PREVENTION_ENTITY_EDIT_PREVENTION, {
        entityId,
        type: session.preventionType,
        sessionId: session.id
      })
    );
  };
  return (
    <Button
      {...props}
      onClick={onEditPreventionHandler}
      dataQa={`${slugify(
        messages.preventionAddExtraTestsButton.id.toLowerCase()
      )}-menu`}
    >
      <Icon id={props?.menu ? 'add' : 'add-white'} />
      <FormattedMessage {...messages.preventionAddExtraTestsButton} />
    </Button>
  );
};
